/**
 * メール本文 plain text レンダラ（admin-web / functions 共通）。
 * 仕様書: docs/01_spec/spec_mail_system.md B-9
 *
 * DOM/Browser API は使わない（Node / Browser 両対応）。
 */
import type { MailSection } from './types/mail';

export interface RenderMailBodyTextOptions {
  /** {{CTA_URL}} を実 URL に上書き（配信予約の ctaUrl 用） */
  ctaUrlOverride?: string;
}

/**
 * インライン Markdown 記号を除去する（プレーンテキスト出力用）。
 * AI 生成本文に混ざる `**太字**` / `*斜体*` の `*` 記号を消し、中身だけ残す。
 * プレーンテキストには太字概念が無いため「装飾を外す」のが正しい挙動。
 * 太字を保ちたい場合は inlineMarkdownToHtml（HTML 出力）を使う。
 */
export function stripInlineMarkdown(text: string): string {
  return text
    .replace(/\*\*([^*]+?)\*\*/g, '$1')
    .replace(/\*([^*\n]+?)\*/g, '$1');
}

/**
 * インライン Markdown を HTML タグへ変換する（HTML 出力用）。
 * 必ず HTML エスケープ済みの文字列に対して適用すること
 * （挿入する <strong>/<em> タグだけを安全に残すため）。
 */
export function inlineMarkdownToHtml(escaped: string): string {
  return escaped
    .replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*\n]+?)\*/g, '<em>$1</em>');
}

export function renderMailBodyText(
  sections: MailSection[],
  opts: RenderMailBodyTextOptions = {},
): string {
  const { ctaUrlOverride } = opts;
  return sections
    .map((s) => {
      let content = s.content;
      if (s.type === 'cta') {
        // 優先順位: ctaUrlOverride（配信予約 / カード経由）> セクション既定値
        // どちらも空ならリテラル `{{CTA_URL}}` を本文に残さず空文字へ置換し、
        // URL の追記もしない（LINE のレンダラと同じ挙動に揃える）。
        const rawUrl =
          (ctaUrlOverride && ctaUrlOverride.trim()) ||
          (s.cta_url_placeholder && s.cta_url_placeholder.trim()) ||
          '';
        // プレースホルダ自身（{{CTA_URL}}）が cta_url_placeholder に入っていた場合は
        // 「実 URL なし」と同じ扱い（誤って受信者に晒さない）
        const url = rawUrl === '{{CTA_URL}}' ? '' : rawUrl;
        content = content.replace(/\{\{CTA_URL\}\}/g, url);
        if (url && !content.includes(url)) {
          content += `\n${url}`;
        }
      }
      return stripInlineMarkdown(content);
    })
    .join('\n\n');
}

/**
 * URL に audience 識別子（cid / lid）と流入元タグ（src）と campaignId を注入する。
 * 既に同じパラメータが付与されている場合は何もしない（冪等）。
 *
 * - `/public/forms/...` `/l/...` を含む URL のみ対象
 * - 外部 URL（外部ドメイン）は変更なし
 *
 * `src` は P1（campaign analytics）で導入。受信側が CV を流入元別に集計するために使う。
 *  - mail dispatcher → src='mail'
 *  - LINE dispatcher → src='line'
 *  - LP 内のフォーム CTA → src='landing'
 *
 * `campaignId` は 1:N（1 ランディングを複数キャンペーンで使い回す）対応で追加。
 * 配信時のキャンペーン context を URL に焼き込み、LP 着地後の CTA リンクに
 * 引き継ぐ（PublicLandingPage が ?campaignId= を読み取って優先する）。
 */
export function injectAudienceId(
  url: string | null | undefined,
  audience: {
    cid?: string | null;
    lid?: string | null;
    src?: string | null;
    campaignId?: string | null;
  } | null,
): string {
  if (!url) return url ?? '';
  if (!audience) return url;
  if (!url.includes('/public/forms/') && !url.includes('/l/')) return url;

  const params: Array<[string, string]> = [];
  if (audience.cid && !/[?&]cid=/.test(url)) {
    params.push(['cid', audience.cid]);
  }
  if (audience.lid && !/[?&]lid=/.test(url)) {
    params.push(['lid', audience.lid]);
  }
  if (audience.src && !/[?&]src=/.test(url)) {
    params.push(['src', audience.src]);
  }
  if (audience.campaignId && !/[?&]campaignId=/.test(url)) {
    params.push(['campaignId', audience.campaignId]);
  }
  if (params.length === 0) return url;

  const [pathPart, hashPart] = url.split('#');
  const sep = pathPart.includes('?') ? '&' : '?';
  const query = params.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
  const newPath = `${pathPart}${sep}${query}`;
  return hashPart ? `${newPath}#${hashPart}` : newPath;
}
